import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { MapPin, CarFront, Heart, Baby, Mountain, CloudRain, Ghost, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";
import { ATTRACTIONS } from "@/staticData";
import { useFavorites } from "@/favorites";
import { fmtDate } from "@/api";
import AttractionsMap from "@/components/AttractionsMap";

const CATS = ["aproape", "copii", "castele", "history_faith", "natura", "adrenalina", "dark", "iarna", "nightlife", "solo", "taste", "saxon"];

export default function Attractions({ config }) {
  const { t, lang } = useLang();
  const [cat, setCat] = useState("all");
  const { favs, toggle } = useFavorites();
  const lastChecked = ATTRACTIONS.map((a) => a.last_verified).filter(Boolean).sort().pop();

  const items = useMemo(() => {
    const active = ATTRACTIONS.filter((a) => a.active !== false);
    const filtered = cat === "all" ? active : active.filter((a) => a.categories.includes(cat));
    return [...filtered].sort((a, b) => (a.sort_order - b.sort_order) || ((b.featured ? 1 : 0) - (a.featured ? 1 : 0)));
  }, [cat]);

  return (
    <section id="atractii" className="py-24 md:py-32 bg-cream-light border-y border-warm-grey/30" data-testid="attractions-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <motion.p initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8 }}
          className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-walnut flex items-center gap-3">
          <span className="inline-block w-10 h-px bg-terracotta" />
          {t("att.eyebrow")}
        </motion.p>
        <div className="mt-5 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-medium text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-main max-w-2xl">
            {t("att.title")}
          </motion.h2>
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.2 }}
            className="text-brown/80 font-light max-w-md">
            {t("att.sub")}
          </motion.p>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.8, delay: 0.15 }}
          className="mt-10 flex flex-wrap gap-2" data-testid="attraction-filters">
          <button onClick={() => setCat("all")} data-testid="att-filter-all"
            className={`rounded-full px-4 py-2 text-xs font-bold tracking-wider uppercase transition-all duration-300 ${cat === "all" ? "bg-main text-cream-light" : "bg-cream border border-warm-grey/60 text-brown hover:border-terracotta"}`}>
            {t("attx.all")}
          </button>
          {CATS.map((c) => (
            <button key={c} onClick={() => setCat(c)} data-testid={`att-filter-${c}`}
              className={`rounded-full px-4 py-2 text-xs font-bold tracking-wider uppercase transition-all duration-300 ${cat === c ? "bg-main text-cream-light" : "bg-cream border border-warm-grey/60 text-brown hover:border-terracotta"}`}>
              {t(`attx.cats.${c}`)}
            </button>
          ))}
        </motion.div>
        {lastChecked && (
          <p className="mt-4 text-[11px] text-brown/50 font-light" data-testid="attractions-verified">
            {t("ev.verified").replace("{d}", fmtDate(lastChecked))}
          </p>
        )}
        <div className="mt-8 grid md:grid-cols-2 gap-5">
          {items.map((a, i) => (
            <motion.article
              key={a.id}
              initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ duration: 0.6, delay: Math.min(i * 0.05, 0.4) }}
              className="group bg-cream border border-warm-grey/40 rounded-3xl p-6 hover:border-terracotta/50 hover:shadow-xl hover:shadow-terracotta/5 transition-all duration-300"
              data-testid={`attraction-${a.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex flex-wrap gap-1.5">
                  {a.categories.slice(0, 3).map((c) => (
                    <span key={c} className="text-[10px] tracking-[0.15em] uppercase font-bold text-terracotta bg-terracotta/10 rounded-full px-2.5 py-1">
                      {t(`attx.cats.${c}`)}
                    </span>
                  ))}
                </div>
                <button onClick={() => toggle(a.id)} data-testid={`att-fav-${a.id}`} aria-label={t("attx.fav_add")}
                  className={`shrink-0 w-9 h-9 rounded-full border flex items-center justify-center transition-all duration-300 ${favs.includes(a.id) ? "border-terracotta text-terracotta bg-terracotta/10" : "border-warm-grey/60 text-warm-grey hover:text-terracotta hover:border-terracotta"}`}>
                  <Heart size={15} className={favs.includes(a.id) ? "fill-terracotta" : ""} />
                </button>
              </div>

              <h3 className="mt-3 font-serif text-2xl text-main group-hover:text-terracotta transition-colors duration-300">{a[`name_${lang}`] || a.name}</h3>
              <p className="mt-2 text-sm text-brown/80 font-light leading-relaxed">{a[`desc_${lang}`] || a.desc_en || a.desc_ro}</p>

              <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-brown/70">
                {a.distance_km > 0 && (
                  <span className="flex items-center gap-1.5 font-semibold text-main">
                    <MapPin size={13} className="text-terracotta" />
                    {String(a.distance_km).replace(".", ",")} {t("att.km")}
                  </span>
                )}
                {a.travel_time && (
                  <span className="flex items-center gap-1.5">
                    <CarFront size={13} className="text-sage" /> {t("attx.approx")} {a.travel_time}
                  </span>
                )}
                {a.kids && <span className="flex items-center gap-1 text-green-dark font-semibold"><Baby size={13} /> {t("attx.kids")}</span>}
                {a.adventure >= 3 && <span className="flex items-center gap-1 text-terracotta font-semibold"><Mountain size={13} /> {t("attx.adventure")}</span>}
                {a.website && (
                  <a href={a.website} target="_blank" rel="noreferrer" data-testid={`att-site-${a.id}`}
                    className="flex items-center gap-1 font-semibold text-brown hover:text-terracotta transition-colors">
                    <ExternalLink size={12} /> {t("ev.official")}
                  </a>
                )}
              </div>

              {a.categories.includes("dark") && (
                <p className="mt-3 flex items-start gap-1.5 text-[11px] text-walnut/80 italic" data-testid={`att-legend-${a.id}`}>
                  <Ghost size={12} className="shrink-0 mt-0.5" /> {t("attx.legend_note")}
                </p>
              )}
              {a.season !== "all" && (
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-walnut/80" data-testid={`att-season-${a.id}`}>
                  <CloudRain size={12} className="shrink-0 mt-0.5" /> {t("attx.seasonal")}
                </p>
              )}
              {a.id === "att-flightaddicted" && (
                <p className="mt-2 flex items-start gap-1.5 text-[11px] text-walnut/80" data-testid="att-weather-flight">
                  <CloudRain size={12} className="shrink-0 mt-0.5" /> {t("attx.weather")}
                </p>
              )}
            </motion.article>
          ))}
        </div>

        <AttractionsMap items={items} config={config} />
      </div>
    </section>
  );
}
